import { useEffect, useRef } from 'react';

/**
 * Risolve l'URL WS:
 * - prova REACT_APP_WS_URL (CRA)
 * - prova VITE_WS_URL (se bundler la inietta su process.env)
 * - fallback a ws(s)://<host>/ws
 */
function resolveWsUrl() {
  const env = (typeof process !== 'undefined' && process.env) ? process.env : {};
  const fromEnv = env.REACT_APP_WS_URL || env.VITE_WS_URL;
  
  console.log('[WS Client] Environment variables:', { fromEnv, processEnv: env });
  
  if (fromEnv) {
    console.log('[WS Client] Using URL from environment:', fromEnv);
    return fromEnv;
  }

  // FIXED: Usa sempre l'host e porta correnti del frontend
  // Se il frontend è servito da nginx su :8080, il WS deve passare per nginx
  const proto = window.location.protocol === 'https:' ? 'wss' : 'ws';
  const baseUrl = `${proto}://${window.location.host}/ws`;
  console.log('[WS Client] Using fallback base URL:', baseUrl);
  console.log('[WS Client] Current location:', window.location.host);
  return baseUrl;
}

/**
 * Hook WS con reconnect (backoff esponenziale).
 * onEvent(event, data, rawMsg) riceve messaggi nel formato: { event, data, ts }
 */
export function useWebSocket({ token, tenantId, onEvent, enabled = true }) {
  const wsRef = useRef(null);
  const retryRef = useRef(0);
  const timerRef = useRef(null);

  useEffect(() => {
    console.log('[WS Client] Hook effect triggered:', { enabled, token: !!token, tenantId });
    
    if (!enabled || !token || !tenantId) {
      console.log('[WS Client] Not connecting - missing requirements');
      return;
    }

    let closed = false;

    const connect = () => {
      console.log('[WS Client] Attempting to connect...');
      const baseUrl = resolveWsUrl();
      
      // Costruisci URL con query parameters
      const url = `${baseUrl}?token=${encodeURIComponent(token)}&tenantId=${encodeURIComponent(tenantId)}`;
      console.log('[WS Client] Connection URL:', url.replace(/token=[^&]+/, 'token=[REDACTED]')); // Log senza token
      
      // FIXED: Rimuovi i protocols per evitare problemi con nginx
      // Il server può comunque gestire l'autenticazione tramite token nell'URL
      console.log('[WS Client] Connecting without protocols to avoid nginx issues');
      
      const ws = new WebSocket(url);
      wsRef.current = ws;

      ws.onopen = (event) => {
        // reset backoff al connect riuscito
        retryRef.current = 0;
        console.log('[WS Client] Connected successfully');
        console.log('[WS Client] Selected protocol:', ws.protocol);
        console.log('[WS Client] ReadyState:', ws.readyState);
      };

      ws.onmessage = (event) => {
        console.log('[WS Client] Raw message received:', event.data);
        try {
          const msg = JSON.parse(event.data); // { event, data, ts }
          console.log('[WS Client] Parsed message:', msg);
          if (msg && msg.event && onEvent) {
            console.log('[WS Client] Calling onEvent with:', msg.event, msg.data);
            onEvent(msg.event, msg.data, msg);
          }
        } catch (err) {
          console.error('[WS Client] Error parsing message:', err);
          console.log('[WS Client] Raw data was:', event.data);
        }
      };

      ws.onerror = (error) => {
        console.error('[WS Client] WebSocket error:', error);
        console.log('[WS Client] ReadyState at error:', ws.readyState);
      };

      ws.onclose = (e) => {
        console.warn('[WS Client] Connection closed:', {
          code: e.code,
          reason: e.reason,
          wasClean: e.wasClean,
          readyState: ws.readyState
        });
        
        wsRef.current = null;
        if (closed) {
          console.log('[WS Client] Connection was intentionally closed, not reconnecting');
          return;
        }
        
        // backoff: 1s -> 2s -> 4s ... max 30s
        const backoff = Math.min(1000 * Math.pow(2, retryRef.current), 30000);
        console.log('[WS Client] Reconnecting in', backoff, 'ms (attempt', retryRef.current + 1, ')');
        retryRef.current += 1;
        timerRef.current = setTimeout(connect, backoff);
      };
    };

    connect();

    return () => {
      console.log('[WS Client] Cleaning up WebSocket connection');
      closed = true;
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      const s = wsRef.current;
      if (s && s.readyState === WebSocket.OPEN) {
        console.log('[WS Client] Closing connection');
        s.close();
      }
      wsRef.current = null;
    };
  }, [enabled, token, tenantId, onEvent]);

  return {
    getSocket: () => wsRef.current,
  };
}